import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./AuthProvider";
import { GoogleOAuthProvider } from "@react-oauth/google";

import "./assets/styles/styles.scss";
import "./cabinet/css/layout.css";

import Home from "./pages/Home";
import MyFleet from "./pages/MyFleet";
import Login from "./pages/Login";
import Registration from "./pages/Registration";
import CreateDriver from "./pages/CreateDriver";
import OrderEdit from "./pages/OrderEdit";
import DriversList from "./pages/DriversList";
import Protected from "./Protected";
import Orders from "./pages/Orders";
import OrderForm from "./components/orders/OrderForm";
import OrderDetail from "./components/search/detailed-order/OrderDetail";
import TransportDetail from "./components/search/detailed-transport/TransportDetail";
import SearchOrders from "./pages/SearchOrders";
import Dashboard from "./pages/Dashboard";
import { cabinetRoutes } from "./cabinet/routes";
import SearchTransports from "./pages/SearchTransports";
// import SearchAds from './pages/SearchAds';
import MyOrders from "./components/my-orders/MyOrders";
import Notifications from "./pages/Notifications";
import Companies from "./pages/Companies";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import Landing from "./pages/Landing";
import { clearAuthTokens, GOOGLE_CLIENT_ID } from "./utils/operatorAuth";
import { getOperatorUrl, isPublicHost, PUBLIC_ORIGIN } from "./config/site";

const RouteChangeOverlay = () => {
  const location = useLocation();
  const [prevLocation, setPrevLocation] = useState(location.pathname);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Update state during render to show overlay synchronously on route change
  if (location.pathname !== prevLocation) {
    setPrevLocation(location.pathname);
    setIsTransitioning(true);
  }

  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(false), 300); // 2-3 seconds delay
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  const isSmallGrid = localStorage.getItem('display-grid');

  if (!isTransitioning || isSmallGrid !== 'small-grid') return null;

  return (
    <div
      className="transition-hide-on-mob"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f0f4f7",
        zIndex: 9999,
      }}
    />
  );
};

const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
};

const OperatorLogout = () => {
  useEffect(() => {
    clearAuthTokens();
    window.location.replace(PUBLIC_ORIGIN);
  }, []);

  return null;
};

/** The legacy shell pins html/body/#root to 100% and sets `body { overflow: hidden }`; public pages
 *  (landing, privacy, terms) are normal documents and must scroll — same switch the cabinet uses. */
const PublicRoutes = () => {
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("cab-active");
    return () => html.classList.remove("cab-active");
  }, []);
  return <PublicRouteTable />;
};

const PublicRouteTable = () => (
  <Routes>
    <Route path="/" element={<Landing />} />
    <Route path="/landing" element={<Navigate to="/" replace />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
    <Route
      path="/login"
      element={<ExternalRedirect to={getOperatorUrl("/login")} />}
    />
    <Route
      path="/registration"
      element={<ExternalRedirect to={getOperatorUrl("/registration")} />}
    />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const OperatorRoutes = () => (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <RouteChangeOverlay />
      <AuthProvider>
        <Routes>
            {/* Wireframe cabinet (full-width shell) — see src/cabinet */}
            {cabinetRoutes}
            {/* Legacy map shell: only detail/edit pages that have no wireframe yet */}
            {/* Home component will be the wrapper for all dashboard routes */}
            <Route
              path="/"
              element={
                <Protected>
                  <Home />
                </Protected>
              }
            >
              {/* Nested routes inside Home */}
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="orders/create" element={<OrderForm />} />
              <Route path="orders/edit/:id" element={<OrderForm />} />
              <Route path="orders/duplicate/:id" element={<OrderForm />} />
              <Route path="transports/:id" element={<TransportDetail />} />
              <Route path="ads" element={<Navigate to="/my-fleet" replace />} />
              <Route path="ads/:id" element={<Navigate to="/my-fleet" replace />} />
              <Route path="my-ad/:id" element={<Navigate to="/my-fleet" replace />} />
              <Route path="my-ad/create" element={<Navigate to="/my-fleet" replace />} />
              {/* <Route path="my-ads/create" element={<SearchAds />} /> */}
              {/* Index route (default route inside Home) - always shows Search */}
            </Route>
            {/* Other standalone routes */}
            <Route
              path="/drivers/create"
              element={
                <Protected>
                  <CreateDriver />
                </Protected>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <Protected>
                  <OrderEdit />
                </Protected>
              }
            />
            <Route
              path="/orders"
              element={
                <Protected>
                  <Orders />
                </Protected>
              }
            />
            <Route
              path="/drivers"
              element={
                <Protected>
                  <DriversList />
                </Protected>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/recover" element={<Navigate to="/login" replace />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </GoogleOAuthProvider>
);

const AppRoutes = () => {
  const location = useLocation();

  if (isPublicHost()) {
    return <PublicRoutes />;
  }

  if (location.pathname === "/logout") {
    return <OperatorLogout />;
  }

  return <OperatorRoutes />;
};

const App = () => {
  const isIOSChrome = /CriOS/i.test(navigator.userAgent);

  useEffect(() => {
    if (isIOSChrome) {
      document.body.classList.add("ios-chrome");
    } else {
      document.body.classList.remove("ios-chrome");
    }
  }, [isIOSChrome]);

  return (
    <div className="app">
      <Router>
        <AppRoutes />
      </Router>
    </div>
  );
};

export default App;
